import config from '../config'

export interface ReleaseCreateRequest {
  train_run_id: number
  train_artifact_id: number
}

export interface ReleaseResponse {
  id: number
  train_run_id: number
  train_artifact_id: number
  model_file_name: string
  meta_file_name: string
  status: string
  error_message?: string | null
  created_at: string
  uploaded_at?: string | null
}

// 백엔드 release_service.build_meta_json() 이 생성하는 구조 (모든 섹션/필드는 optional)
export interface ReleaseMetaPerson {
  name?: string
  email?: string
}

export interface ReleaseMetaJson {
  model_file?: {
    name?: string
    format?: string
    size_bytes?: number
  }
  training?: {
    created_at?: string | null
    started_at?: string | null
    finished_at?: string | null
    status?: string
    description?: string
    project_name?: string
    task_name?: string
    inputs?: {
      required?: Record<string, any>
      extras?: Record<string, any>
    }
    created_by?: ReleaseMetaPerson
    metrics?: Record<string, any>
    training_env_info?: Record<string, any>
  }
  dataset?: {
    name?: string
    label_version_name?: string
    label_format?: string
    split?: { total?: number; train?: number; val?: number; test?: number }
    sampling?: { method?: string; splits?: any; seed?: number; max_per_class?: number }
    output?: { task?: string; format?: string; created_by?: ReleaseMetaPerson }
  }
  generated?: {
    generated_at?: string
    generated_by?: ReleaseMetaPerson
  }
}

export interface ReleaseDetailResponse extends ReleaseResponse {
  bucket: string
  region: string
  s3_prefix_date: string
  model_key: string
  meta_key: string
  meta_json: ReleaseMetaJson | null
}

class ReleaseService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async create(body: ReleaseCreateRequest): Promise<ReleaseDetailResponse> {
    const resp = await fetch(`${this.baseUrl}/releases`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async list(query?: { run_id?: number; page?: number; size?: number }): Promise<ReleaseResponse[]> {
    const params = new URLSearchParams()
    if (query?.run_id) params.set('run_id', String(query.run_id))
    if (query?.page) params.set('page', String(query.page))
    if (query?.size) params.set('size', String(query.size))
    const resp = await fetch(`${this.baseUrl}/releases?${params.toString()}`, {
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async get(releaseId: number): Promise<ReleaseDetailResponse> {
    const resp = await fetch(`${this.baseUrl}/releases/${releaseId}`, {
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async retry(releaseId: number): Promise<ReleaseDetailResponse> {
    const resp = await fetch(`${this.baseUrl}/releases/${releaseId}/retry`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }
}

export default new ReleaseService()

